// src/App.js
import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { DataProvider } from './context/DataContext';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { Elements } from '@stripe/react-stripe-js';
import { CookieConsentProvider } from './context/CookieConsentContext';

import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import HowItWorksPage from "./pages/HowItWorksPage";
import CatalogPage from "./pages/CatalogPage";
import IndustryPage from './pages/IndustryPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import ThankYouPage from './pages/ThankYouPage';
import RegistrationPage from './pages/RegistrationPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import OrderHistoryPage from './pages/OrderHistoryPage';
import SellerDashboard from './pages/SellerDashboard';
import SearchPage from './pages/SearchPage';
import NotFoundPage from './pages/NotFoundPage';
import ActivateAccount from './pages/ActivateAccount';
import SuccessPage from './pages/SuccessPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LegalPage from './pages/LegalPage';
import MaintenancePage from './pages/MaintenancePage';
import Footer from './components/Home/Footer';

import ScrollToTop from './components/layout/ScrollToTop';
import Toast from './components/common/Toast';
import { useToast } from './context/ToastContext';
import './App.css';

// Admin-näkymä + roolivartija
import AdminSuppliersBoard from './pages/AdminSuppliersBoard';
import RequireAdmin from './components/admin/RequireAdmin';

let stripePromise;

function getStripePromise() {
  if (!stripePromise) {
    stripePromise = require('./stripe/stripeClient').default;
  }

  return stripePromise;
}

function ToastContainer() {
  const { toast, hideToast } = useToast();
  return toast ? (
    <Toast
      message={toast.message}
      type={toast.type}
      onClose={hideToast}
    />
  ) : null;
}

function App() {
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  const activeStripePromise = getStripePromise();

  return (
    <CookieConsentProvider>
      <DataProvider>
        <AuthProvider>
          <CartProvider>
            <Elements stripe={activeStripePromise}>
              <ToastContainer />
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="/how-it-works" element={<HowItWorksPage />} />
                  <Route path="login" element={<LoginPage />} />
                  <Route path="register" element={<RegistrationPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="orders" element={<OrderHistoryPage />} />

                  {/* Seller-dashboard: vain admin-käyttäjille */}
                  <Route
                    path="seller/dashboard"
                    element={
                      <RequireAdmin>
                        <SellerDashboard />
                      </RequireAdmin>
                    }
                  />

                  <Route path="search" element={<SearchPage />} />
                  <Route path="/catalog" element={<CatalogPage />} />
                  <Route path=":industrySlug" element={<IndustryPage />} />
                  <Route
                    path=":industrySlug/product/:productId"
                    element={<ProductDetailPage />}
                  />
                  <Route path="checkout" element={<CheckoutPage />} />
                  <Route path="/kiitos" element={<ThankYouPage />} />
                  <Route
                    path="activate-account"
                    element={<ActivateAccount />}
                  />
                  <Route
                    path="forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="reset-password"
                    element={<ResetPasswordPage />}
                  />
                  <Route path="legal" element={<LegalPage />} />
                  <Route path="/success" element={<SuccessPage />} />

                  {/* Admin / Suppliers Board (vain admin-roolille) */}
                  <Route
                    path="/admin/suppliers-board"
                    element={
                      <RequireAdmin>
                        <AdminSuppliersBoard />
                      </RequireAdmin>
                    }
                  />

                  {/* Aliakset/404 */}
                  <Route path="account" element={<ProfilePage />} />
                  <Route
                    path="account/orders"
                    element={<OrderHistoryPage />}
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
              <Footer />
            </Elements>
          </CartProvider>
        </AuthProvider>
      </DataProvider>
    </CookieConsentProvider>
  );
}

export default App;
